import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/system";
import { Box, Button, List, Card, CardMedia, CardContent } from "@mui/material";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import { lighten, darken, alpha } from "@mui/material/styles";

import Paper from "@mui/material/Paper";
export const HeroSection = styled(Paper)(({ theme, role }) => ({
  position: "relative",
  color: "white",
  padding: theme.spacing(5, 0),
  marginBottom: theme.spacing(6),
  borderRadius: theme.shape.borderRadius * 5,
  background:
    theme.palette.themeType === "allakando"
      ? "linear-gradient(105deg, rgb(121, 162, 33) 0%, rgba(121, 162, 33, 0.95) 50%, rgba(121, 162, 33, 0.98) 100%)"
      : role === "student"
        ? "linear-gradient(105deg, rgba(25, 118, 210, 1) 0%, rgba(21, 101, 192, 0.95) 50%, rgba(13, 71, 161, 0.98) 100%)"
        : "linear-gradient(135deg, rgba(139, 92, 246, 1) 0%, rgba(147, 51, 234, 0.95) 50%, rgba(139, 92, 246, 0.9) 100%)",
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(0deg, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
    backgroundSize: "20px 20px",
  },
}));

export const StepCircle = styled(Box)(({ theme, filled }) => ({
  width: 58,
  height: 58,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: filled
    ? theme.palette.primary.main
    : theme.palette.background.paper,
  border: filled ? "none" : `2px solid ${theme.palette.primary.main}`,
  color: filled
    ? theme.palette.primary.contrastText
    : theme.palette.primary.main,
  fontSize: "1.45rem",
  marginBottom: theme.spacing(3),
  fontWeight: "600",
}));

export const StyledCard = styled(Card)(({ theme, isLarge }) => ({
  height: "100%",
  display: "flex",
  flexDirection: isLarge ? "row" : "column",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
  //boxShadow: "0 3px 28px rgba(12,8,51,.06)",
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  borderRadius: theme.shape.borderRadius * 4,
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
    "& .MuiCardMedia-root": {
      transform: "scale(1.05)",
    },
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: theme.palette.primary.main,
    transform: "scaleX(0)",
    transition: "transform 0.3s ease",
  },
  "&:hover::before": {
    transform: "scaleX(1)",
  },
}));

// Update CardMediaStyled for different layouts
export const CardMediaStyled = styled(CardMedia)(({ theme, isLarge }) => ({
  height: isLarge ? "auto" : 180,
  width: isLarge ? 180 : "auto",
  // backgroundColor: theme.palette.grey[50],
  transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(3),
  position: "relative",
  backgroundSize: "cover",
  backgroundPosition: "center",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
}));
export const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: theme.palette.primary.main,
  position: "relative",
  zIndex: 1,
}));

export const CardContentStyled = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  textAlign: "left",
  padding: theme.spacing(3),
}));

export const DashedPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  minHeight: 300,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mb: 0,
  borderRadius: theme.shape.borderRadius * 2,
  border: "2px dashed",
  boxShadow: "none",
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(145deg, 
       ${alpha(theme.palette.primary.main, 0.02)} 0%, 
       ${alpha(theme.palette.primary.main, 0.08)} 100%)`
      : `linear-gradient(145deg, 
       ${lighten(theme.palette.primary.main, 0.99)} 0%, 
       ${lighten(theme.palette.primary.main, 0.94)} 100%)`,
  cursor: "pointer",
  borderColor: alpha(theme.palette.primary.main, 0.3),
  transition: "border-color 0.3s ease, opacity 0.3s ease", // Changed to opacity for smooth transition
  position: "relative",
  "&:before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: theme.shape.borderRadius * 2 - 2, // Account for border width
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(145deg, 
         ${alpha(theme.palette.primary.main, 0.05)} 0%, 
         ${alpha(theme.palette.primary.main, 0.15)} 100%)`
        : `linear-gradient(145deg, 
         ${lighten(theme.palette.primary.main, 0.97)} 0%, 
         ${lighten(theme.palette.primary.main, 0.9)} 100%)`,
    opacity: 0,
    transition: "opacity 0.3s ease",
    zIndex: -1,
  },
  "&:hover": {
    borderColor: theme.palette.primary.main,
    "&:before": {
      opacity: 1,
    },
    // Add animation for the child icon when hovering
    "& > .MuiSvgIcon-root, & svg": {
      transform: "rotate(5deg) scale(1.2)",
      transition: "transform 0.2s ease-in-out",
    },
  },
  // Add base styles for the icon to enable smooth transitions
  "& > .MuiSvgIcon-root, & svg": {
    transform: "rotate(0deg) scale(1)",
    transition: "transform 0.2s ease-in-out",
    position: "relative", // Ensure icons stay above the pseudo-element
    zIndex: 1,
  },
}));

export const OptionButton = ({ children, sx = {}, ...props }) => (
  <Button
    {...props}
    sx={{
      margin: (theme) => theme.spacing(1),
      width: { xs: "100%", md: "45%" },
      textTransform: "none",
      fontSize: "1rem",
      borderRadius: "10px",
      borderColor: (theme) => theme.palette.divider,
      borderBottom: (theme) => `4px solid ${theme.palette.divider}`,
      display: "flex",
      justifyContent: "left",
      textAlign: "left",
      padding: (theme) => theme.spacing(1.5),
      // Add transition for smooth hover effects
      transition: "all 0.3s ease",

      // Apply transition to text content
      "& > span": {
        transition: "transform 0.3s ease",
      },

      // Apply transition to MUI icons
      "& .MuiSvgIcon-root": {
        color: (theme) => theme.palette.text.main,
        transition: "transform 0.3s ease",
      },

      "&:hover": {
        borderColor: (theme) => theme.palette.divider,
        borderBottom: (theme) => `4px solid ${theme.palette.divider}`,
        backgroundColor: (theme) => theme.palette.background.paperHover,

        // Move text slightly to the right on hover
        "& > span": {
          transform: "translateX(5px)",
        },
        "& .optionIcon": {
          transform: "translateX(5px)",
        },
        // Move icons slightly to the right on hover
        "& .MuiSvgIcon-root": {
          transform: "translateX(5px)",
        },
      },

      "&.MuiButton-contained": {
        outline: "none",
        backgroundColor: (theme) => theme.palette.text.marked,
        fontWeight: "bold",
        color: (theme) => theme.palette.text.white,
        borderColor: (theme) => darken(theme.palette.text.marked, 0.2),
        "& .MuiSvgIcon-root": {
          color: (theme) => theme.palette.text.white,
        },
      },

      // Allow background color to be overridden
      backgroundColor: (theme) => theme.palette.background.paper,
      ...sx,
    }}
  >
    {children}
  </Button>
);
//

export const GradientBackground = styled(Box)(({ theme, noSvg = false }) => ({
  position: "relative",
  color: theme.palette.common.white,
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "radial-gradient(circle at center, rgb(106, 80, 243) 0%, rgb(62, 46, 141) 82%)",
    zIndex: 1,
  },
  "&::after": {
    content: noSvg ? "none" : '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: noSvg ? "none" : 'url("/img/hepp.svg")',
    backgroundPosition: "center",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    opacity: 0.7,
    zIndex: 2,
  },
  "& > *": {
    position: "relative",
    zIndex: 3,
  },
}));

export const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "50px",
  padding: "12px 30px",
  fontWeight: "bold",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(107, 70, 193, 0.4)",
  },
}));

export const FeatureSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  background: theme.palette.background.default,
  position: "relative",
}));

export const FeatureList = styled(List)(({ theme }) => ({
  "& .MuiListItem-root": {
    "& .MuiListItemIcon-root": {
      // Target the icon INSIDE the list item
      backgroundColor: "transparent !important",
      "&:hover": {
        backgroundColor: "transparent !important",
      },
    },
  },
  "& *": {
    pointerEvents: "none",
    transition: "none !important",
    "&:hover": {
      backgroundColor: "transparent !important",
      color: "inherit !important",
      transform: "none !important",
      boxShadow: "none !important",
    },
  },
}));

export const ResponsiveVideo = ({
  light,
  dark,
  alt,
  maxWidth,
  theme,
  styled = false,
}) => {
  const videoRef = useRef(null);

  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const videoSrc = theme.palette.mode === "dark" ? dark : light;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      if (isVisible) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [isVisible]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [videoSrc]);

  const videoStyle = {
    width: "100%",
    height: "auto",
    borderRadius: "13px",
    ...(styled && {
      border: "2px solid #e8e8e8",
      boxShadow:
        "rgba(0, 0, 0, 0.07) 0px 1px 2px, rgba(0, 0, 0, 0.07) 0px 2px 4px, rgba(0, 0, 0, 0.07) 0px 4px 8px, rgba(0, 0, 0, 0.07) 0px 8px 16px, rgba(0, 0, 0, 0.07) 0px 16px 32px, rgba(0, 0, 0, 0.07) 0px 32px 64px",
    }),
  };

  return (
    <Link to="/app" style={{ textDecoration: "none" }}>
      <div ref={containerRef} style={{ maxWidth: `100%`, margin: "0 auto" }}>
        <video
          ref={videoRef}
          key={videoSrc}
          loop
          muted
          playsInline
          style={videoStyle}
        >
          <source src={videoSrc} type="video/mp4" />
          {alt}
        </video>
      </div>
    </Link>
  );
};
